import { ColorInput as MantineColorInput, ColorInputProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

const DEFAULT_SWATCHES = [
  '#868e96', '#fa5252', '#e64980', '#be4bdb',
  '#7950f2', '#4c6ef5', '#228be6', '#15aabf',
  '#12b886', '#40c057', '#82c91e', '#fab005', '#fd7e14',
];

export interface ThemedColorInputProps extends ColorInputProps {
  gradientFocus?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean;
}

const ColorInput = forwardRef<HTMLInputElement, ThemedColorInputProps>(
  ({ gradientFocus = true, gradientVariant = 'primary', useColorScheme = true, swatches = DEFAULT_SWATCHES, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradientFocus) {
      return (
        <MantineColorInput
          ref={ref}
          swatches={swatches}
          styles={{
            ...styles,
            label: {
              textAlign: 'left !important' as any,
              ...accessibilityStyles,
              ...(styles as any)?.label,
            },
            input: {
              paddingLeft: '36px',
              ...accessibilityStyles,
              ...(styles as any)?.input,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineColorInput
        ref={ref}
        swatches={swatches}
        styles={{
          ...styles,
          label: {
            textAlign: 'left !important' as any,
            ...accessibilityStyles,
            ...(styles as any)?.label,
          },
          input: {
            paddingLeft: '36px',
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            '&:focus': {
              borderColor: `${color} !important`,
              boxShadow: strongFocus ? `0 0 0 3px ${color}50 !important` : `0 0 0 2px ${color}30 !important`,
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              borderColor: `${color} !important`,
            },
            ...accessibilityStyles,
            ...(styles as any)?.input,
          },
        }}
        {...rest}
      />
    );
  }
);

ColorInput.displayName = 'ColorInput';

export default ColorInput;
